import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

// ── Context ─────────────────────────────────────────────────────────────────

const TrailContext = createContext(null);

const STORAGE_KEY = 'nav_trail';
const MAX_TRAIL = 10;

// Route → section label mapping
// Toutes les pages "racines" (list pages) qui doivent apparaître comme point de retour
// dans le breadcrumb quand l'utilisateur navigue vers une detail page.
const SECTION_ROOTS = {
  // Onglet Maia (#20550) : questions de Maia auxquelles je peux répondre
  '/maia/attente': { label: 'Maia attend', icon: null },
  // Classement OneDrive (superadmin)
  '/classement': { label: 'Classement', icon: null },
  '/mails': { label: 'Mails', icon: null },
  '/admin/signature': { label: 'Signature électronique', icon: null },
  // Ticketing
  '/tickets': { label: 'Tickets', icon: null },
  '/tickets/analytics': { label: 'Analytics tickets', icon: null },
  '/ticketing/dashboard': { label: 'Dashboard Tickets', icon: null },
  '/reception': { label: 'Réception', icon: null },
  // Incidents & assistance
  '/incidents': { label: 'Incidents', icon: null },
  '/assistant': { label: 'Assistant', icon: null },
  '/assistant/prospects': { label: 'Prospects', icon: null },
  '/assistant/ideas': { label: 'Idées', icon: null },
  '/assistant/appointments': { label: 'Rendez-vous', icon: null },
  '/assistant/notes': { label: 'Notes', icon: null },
  '/assistant/kb-drafts': { label: 'KB Drafts', icon: null },
  // Telephonie
  '/incoming-call': { label: 'Appel entrant', icon: null },
  '/messagerie': { label: 'Messagerie vocale', icon: null },
  '/call-history': { label: "Historique d'appels", icon: null },
  // Calendar & contracts
  '/calendrier': { label: 'Calendrier', icon: null },
  '/calendar': { label: 'Calendrier', icon: null },
  '/contrats': { label: 'Contrats', icon: null },
  // BI / Reports
  '/bi': { label: "Bon d'Intervention", icon: null },
  '/reports': { label: 'Rapports', icon: null },
  '/reports/technician-time': { label: 'Temps technicien', icon: null },
  '/reports/questions-teams': { label: 'Questions Teams', icon: null },
  '/gestion/constats': { label: 'Constats techniciens', icon: null },
  '/reports/my-interventions': { label: 'Mes interventions', icon: null },
  '/reports/migration': { label: 'Migration', icon: null },
  '/reports/age-performance': { label: 'Âge & Performance', icon: null },
  '/reports/exchange': { label: 'Migration Exchange', icon: null },
  // Gestion
  '/gestion/utilisateurs': { label: 'Utilisateurs', icon: null },
  '/gestion/societes': { label: 'Sociétés', icon: null },
  '/gestion/groupes': { label: 'Groupes', icon: null },
  '/gestion/domaines': { label: 'Domaines', icon: null },
  '/gestion/facturation': { label: 'Facturation', icon: null },
  '/gestion/catalogue': { label: 'Catalogue (import)', icon: null },
  '/gestion/produits': { label: 'Produits', icon: null },
  '/gestion/tarification': { label: 'Tarification', icon: null },
  '/gestion/rfq': { label: 'RFQ', icon: null },
  '/gestion/stock': { label: 'Stock', icon: null },
  '/gestion/telephonie': { label: 'Téléphonie', icon: null },
  '/gestion/favoris': { label: 'Favoris', icon: null },
  // Configuration
  '/configuration/modeles': { label: 'Modèles de tâches', icon: null },
  '/configuration/categories': { label: 'Catégories', icon: null },
  '/configuration/favoris': { label: 'Favoris', icon: null },
  '/settings': { label: 'Configuration', icon: null },
  // Parc
  '/parc': { label: 'Parc', icon: null },
  '/parc/postes': { label: 'Postes', icon: null },
  '/parc/ordinateurs': { label: 'Ordinateurs', icon: null },
  '/parc/serveurs': { label: 'Serveurs', icon: null },
  '/parc/masterisation': { label: 'Masterisation', icon: null },
  '/parc/imprimantes': { label: 'Imprimantes', icon: null },
  '/parc/sondes': { label: 'Sondes', icon: null },
  '/parc/sauvegardes': { label: 'Sauvegardes', icon: null },
  '/parc/meteris-backup': { label: 'Meteris Backup', icon: null },
  '/parc/liaisons': { label: 'Liaisons', icon: null },
  '/parc/mesh': { label: 'LAN virtuels', icon: null },
  '/parc/inventaire': { label: 'Inventaire', icon: null },
  // CRM
  '/crm': { label: 'CRM', icon: null },
  '/crm/deals': { label: 'Opportunités', icon: null },
  '/crm/contacts': { label: 'Contacts', icon: null },
  // '/crm/accounts' retirée 2026-08-05 : la section « Comptes » n'existe plus,
  // l'URL redirige vers /gestion/societes (déjà déclarée comme racine de section).
  '/crm/activities': { label: 'Activités', icon: null },
  '/crm/pipeline': { label: 'Pipeline', icon: null },
  '/crm/quotes': { label: 'Devis', icon: null },
  '/crm/calculateur-contrats': { label: 'Calculateur de contrats', icon: null },
  '/crm/calculateur-contrats/bareme': { label: 'Barème des contrats', icon: null },
  '/crm/settings': { label: 'Configuration CRM', icon: null },
  // MCF (Qualiobee)
  '/mcf/learners': { label: 'Apprenants', icon: null },
  '/mcf/sessions': { label: 'Sessions', icon: null },
  // KB
  '/kb': { label: 'Base de connaissances', icon: null },
  '/kb/assist': { label: 'Assistant KB', icon: null },
  '/kb/categories': { label: 'Catégories KB', icon: null },
  // Divers
  '/documents': { label: 'Documents', icon: null },
  '/downloads': { label: 'Téléchargements', icon: null },
  '/sites': { label: 'Sites', icon: null },
  '/eset-licenses': { label: 'Licences ESET', icon: null },
  '/vault': { label: 'Coffre-fort', icon: null },
  '/veille': { label: 'Veille', icon: null },
  '/email-triage': { label: 'Triage email', icon: null },
  '/activity': { label: 'Activité', icon: null },
  '/n8n': { label: 'n8n', icon: null },
  '/dashboard': { label: 'Dashboard', icon: null },
  '/ma-journee': { label: 'Ma journée', icon: null },
  '/pilotage': { label: 'Accueil', icon: null },
  // Admin
  '/admin/backups': { label: 'Sauvegardes admin', icon: null },
  '/admin/qualiobee-backups': { label: 'Sauvegardes Qualiobee', icon: null },
  '/admin/github-backups': { label: 'GitHub backups', icon: null },
  '/admin/spooler': { label: 'Spooler', icon: null },
  '/admin/rules': { label: 'Règles métier', icon: null },
  '/admin/snmp': { label: 'SNMP', icon: null },
  '/admin/docker': { label: 'Docker', icon: null },
  '/admin/scheduled-tasks': { label: 'Tâches planifiées', icon: null },
  '/admin/system-monitor': { label: 'System monitor', icon: null },
  '/admin/e2e-monitor': { label: 'Monitoring E2E', icon: null },
  '/admin/bug-reports': { label: 'Bugs portail', icon: null },
  '/admin/ai-usage': { label: 'Monitoring IA', icon: null },
  '/admin/notification-digests': { label: 'Digests notifications', icon: null },
  '/admin/agent-scripts': { label: 'Scripts agent', icon: null },
  '/admin/agent-groups': { label: 'Groupes agent', icon: null },
  '/admin/agent-campaigns': { label: 'Campagnes agent', icon: null },
  '/admin/deploiement-agent': { label: 'Déploiement agent', icon: null },
  '/admin/proxmox': { label: 'Proxmox', icon: null },
  '/admin/proxmox/vms': { label: 'Proxmox VMs', icon: null },
  '/admin/proxmox/migrate': { label: 'Migration Proxmox', icon: null },
  '/admin/proxmox/pra': { label: 'Proxmox PRA', icon: null },
  '/admin/vmware': { label: 'VMware', icon: null },
  '/admin/vmware/vms': { label: 'VMware VMs', icon: null },
  '/admin/vmware/migrate': { label: 'Migration VMware', icon: null },
  '/admin/vmware/pra': { label: 'VMware PRA', icon: null },
  '/admin/docker/migrate': { label: 'Migration Docker', icon: null },
  '/admin/configuration/imprimantes': { label: 'Imprimantes', icon: null },
  // ALSO Cloud Marketplace
  '/also/admin': { label: 'ALSO Admin', icon: null },
  '/also/billing': { label: 'ALSO Facturation', icon: null },
  '/also/licences': { label: 'ALSO Licences', icon: null },
  '/eset/billing': { label: 'Facturation ESET', icon: null },
  '/acronis/billing': { label: 'Facturation Acronis', icon: null },
  '/amc/billing': { label: 'Facturation AMC', icon: null },
  // Configuration (suite)
  '/configuration/snippets': { label: 'Snippets', icon: null },
  // Gestion (suite — catalogue, panier, meet-meaux)
  '/gestion/catalogue/attributs-inconnus': { label: 'Attributs inconnus', icon: null },
  '/gestion/catalogue/import': { label: 'Import catalogue', icon: null },
  '/gestion/catalogue/jobs': { label: 'Jobs catalogue', icon: null },
  '/gestion/catalogue/revue': { label: 'Revue catalogue', icon: null },
  '/gestion/meet-meaux/reservations': { label: 'Meet Meaux', icon: null },
  '/gestion/meet-meaux/etats-des-lieux': { label: 'États des lieux', icon: null },
  '/gestion/meet-meaux/conventions': { label: 'Conventions', icon: null },
  '/gestion/panier': { label: 'Panier', icon: null },
  '/gestion/rapprochements-auto': { label: 'Rapprochements bancaires', icon: null },
  '/gestion/tiers': { label: 'Tiers & règlements', icon: null },
  // Laboratoire
  '/laboratoire': { label: 'Laboratoire', icon: null },
  // MCF (suite)
  '/mcf/admin/sync': { label: 'Sync Qualiobee', icon: null },
  '/mcf/ateliers': { label: 'Ateliers MCF', icon: null },
  '/mcf/formations': { label: 'Formations MCF', icon: null },
  '/mcf/bpf': { label: 'BPF MCF', icon: null },
  '/mcf/banque': { label: 'Banque MCF', icon: null },
  // Microsoft 365
  '/microsoft365': { label: 'Microsoft 365', icon: null },
  // RDV atelier (it-bookings)
  '/rendez-vous-comptoir': { label: 'RDV atelier', icon: null },
  '/configurateur-admin': { label: 'Configurateur RDV', icon: null },
  // Notifications
  '/notifications': { label: 'Notifications', icon: null },
  // Package builder
  '/package-builder': { label: 'Package builder', icon: null },
  // Parc (suite)
  '/parc/toners': { label: 'Toners', icon: null },
  '/parc/toners/facturation': { label: 'Facturation toners', icon: null },
  // Réseaux sociaux
  '/social': { label: 'Réseaux sociaux', icon: null },
  '/social/calendar': { label: 'Calendrier social', icon: null },
  // Préférences utilisateur
  '/user/preferences': { label: 'Préférences', icon: null },
  // RH
  '/rh': { label: 'RH', icon: null },
  '/rh/registre': { label: 'Registre du personnel', icon: null },
  // Écrans qui n'avaient AUCUN fil d'Ariane (William 02/10/2026) — garde : __tests__/filArianeRoutes.test.js
  '/pages-ia': { label: 'Pages agents', icon: null },
  '/pages-partagees': { label: 'Pages partagées', icon: null },
  '/mes-conges': { label: 'Mes congés', icon: null },
  '/gestion/cloudflare-zones': { label: 'Zones Cloudflare', icon: null },
  '/admin/procedures': { label: 'Procédures', icon: null },
  '/admin/procedures/dashboard': { label: 'Dashboard procédures', icon: null },
  '/admin/catalog-suppliers': { label: 'Imports fournisseurs', icon: null },
  '/admin/catalog-price-anomalies': { label: 'Anomalies prix', icon: null },
  '/admin/seo': { label: 'Monitoring SEO', icon: null },
  '/admin/pc-configs': { label: 'Configurations PC', icon: null },
  '/admin/pc-service-options': { label: 'Options de service PC', icon: null },
  '/admin/agent-install-status': { label: 'Statut agents', icon: null },
  '/admin/meteris-agents': { label: 'Agents Meteris', icon: null },
  '/admin/agents/versions': { label: "Versions de l'agent", icon: null },
  '/admin/agents/heure': { label: 'Heure des postes', icon: null },
  '/admin/agents/sans-societe': { label: 'Postes sans société', icon: null },
  '/admin/sms': { label: 'SMS', icon: null },
  '/admin/configuration/my-meteris': { label: 'Déploiement MyMeteris', icon: null },
  '/admin/eset-task-actions': { label: "ESET — Catalogue d'actions", icon: null },
  '/admin/eset-classification': { label: 'ESET — Classification', icon: null },
  '/admin/eset/trials': { label: 'ESET — Essais', icon: null },
  '/mcf/inscriptions': { label: 'Inscriptions MCF', icon: null },
  '/comptabilite/banque': { label: 'Banque', icon: null },
  '/comptabilite/previsionnel': { label: 'Prévisionnel', icon: null },
  '/admin/banque/mappings': { label: 'Banque · Mappings IBAN', icon: null },
  '/admin/banque/categories': { label: 'Banque · Règles de catégorisation', icon: null },
  '/admin/banque/parsers': { label: 'Banque · Parsers', icon: null },
};

function loadTrail() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveTrail(trail) {
  // Même garde que loadTrail : mode privé / quota → exception dans un effet, sans boundary.
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trail)); } catch { /* ignore */ }
}

/**
 * NavigationTrailProvider wraps the app and tracks breadcrumb navigation.
 *
 * Usage in detail pages:
 *   const { pushCrumb } = useTrail();
 *   useEffect(() => { pushCrumb({ path: '/tickets/abc', label: 'Ticket #123' }); }, [ticket]);
 */
export function NavigationTrailProvider({ children }) {
  const [trail, setTrail] = useState(loadTrail);
  const location = useLocation();
  // Provenance déclarée AVANT une navigation transversale (voir setPendingOrigin).
  const pendingOriginRef = useRef(null);

  // Persist trail on change
  useEffect(() => { saveTrail(trail); }, [trail]);

  // When navigating to a section root (list page), re-anchor the trail to this page.
  // This way, when the user then opens a detail page from here, the section root
  // remains in the trail as a return point (ex: Facturation → Ticket #18841).
  //
  // EXCEPTION — navigation transversale entre deux sections racines.
  // Sans elle, aller de Sites au Coffre-fort effaçait le lien de retour vers
  // Sites : les deux étant des SECTION_ROOTS, l'arrivée réinitialisait le trail.
  // Quand la page de départ a déclaré sa provenance, on garde les deux niveaux
  // (« Sites › Coffre-fort ») au lieu d'écraser.
  useEffect(() => {
    const path = location.pathname;
    if (!SECTION_ROOTS[path]) return;
    const destination = { path, label: SECTION_ROOTS[path].label, icon: SECTION_ROOTS[path].icon };
    const origin = pendingOriginRef.current;
    pendingOriginRef.current = null;
    if (origin && origin.path && origin.path !== path) {
      setTrail([origin, destination]);
    } else {
      setTrail([destination]);
    }
  }, [location.pathname]);

  /**
   * Déclare d'où l'on vient, juste AVANT un `navigate()` vers une autre
   * section racine. Sans cela, la destination réinitialise le fil d'Ariane et
   * l'utilisateur perd son chemin de retour.
   * Usage : setPendingOrigin({ path: '/sites', label: 'Sites' }); navigate('/vault?item=…');
   */
  const setPendingOrigin = useCallback((origin) => {
    pendingOriginRef.current = origin || null;
  }, []);

  // Le trail courant, lu au moment de l'appel et non capturé à la construction :
  // `preserveTrailOnNextRoot` doit garder une identité stable (elle est utilisée
  // dans des `useCallback` de pages) tout en voyant le fil le plus récent.
  const trailRef = useRef(trail);
  useEffect(() => { trailRef.current = trail; }, [trail]);

  /**
   * Variante de `setPendingOrigin` qui n'exige AUCUN argument : la provenance
   * est la page où l'on se trouve, c'est-à-dire la dernière miette du fil.
   *
   * À préférer à `setPendingOrigin` partout où l'appelant est déjà inscrit dans
   * le fil : il n'a alors rien à redéclarer, donc rien à faire diverger. Utile
   * aussi dans les composants partagés (modales) qui ignorent depuis quelle page
   * ils sont ouverts.
   * Usage : preserveTrailOnNextRoot(); navigate('/gestion/panier?cart_id=…');
   */
  const preserveTrailOnNextRoot = useCallback(() => {
    const courant = trailRef.current;
    const derniere = courant[courant.length - 1];
    pendingOriginRef.current = derniere ? { ...derniere } : null;
  }, []);

  const pushCrumb = useCallback(({ path, label, icon }) => {
    setTrail(prev => {
      // If this path already exists in the trail, truncate to it (going back)
      const existingIdx = prev.findIndex(c => c.path === path);
      if (existingIdx >= 0) {
        return prev.slice(0, existingIdx + 1);
      }

      // Cross-section navigation is allowed — the trail accumulates
      // (e.g., Company → User → Ticket). It only resets when navigating
      // to a list/root page (handled in the useEffect above).

      // Append
      const next = [...prev, { path, label, icon }];
      return next.length > MAX_TRAIL ? next.slice(-MAX_TRAIL) : next;
    });
  }, []);

  const navigateToCrumb = useCallback((index) => {
    setTrail(prev => prev.slice(0, index + 1));
  }, []);

  const clearTrail = useCallback(() => {
    setTrail([]);
  }, []);

  return (
    <TrailContext.Provider value={{ trail, pushCrumb, navigateToCrumb, clearTrail, setPendingOrigin, preserveTrailOnNextRoot }}>
      {children}
    </TrailContext.Provider>
  );
}

export function useTrail() {
  const ctx = useContext(TrailContext);
  if (!ctx) throw new Error('useTrail must be used within NavigationTrailProvider');
  return ctx;
}

function findSection(path) {
  // Find the matching section root for a given path
  const roots = Object.keys(SECTION_ROOTS).sort((a, b) => b.length - a.length);
  for (const root of roots) {
    if (path === root || path.startsWith(root + '/')) return root;
  }
  return null;
}

// Build the breadcrumb ancestors of a given path, based on SECTION_ROOTS prefixes.
// Ex: "/parc/toners/facturation" -> [{path:"/parc"}, {path:"/parc/toners"}, {path:"/parc/toners/facturation"}].
// Used so that even root pages (ex: /parc/toners) display "Parc > Toners" instead of nothing.
function buildAncestors(path) {
  if (!path) return [];
  const segments = path.split('/').filter(Boolean);
  const out = [];
  for (let i = 1; i <= segments.length; i++) {
    const prefix = '/' + segments.slice(0, i).join('/');
    if (SECTION_ROOTS[prefix]) {
      out.push({ path: prefix, label: SECTION_ROOTS[prefix].label, icon: SECTION_ROOTS[prefix].icon });
    }
  }
  return out;
}

// ── Breadcrumb component ────────────────────────────────────────────────────

export function Breadcrumb() {
  const { trail, navigateToCrumb } = useTrail();
  const navigate = useNavigate();
  const location = useLocation();
  const [portalTarget, setPortalTarget] = useState(null);

  // Find or create a portal container right after .page-header
  useEffect(() => {
    const findTarget = () => {
      // Clean up any orphan portal containers from previous navigation
      document.querySelectorAll('[data-breadcrumb-portal]').forEach(el => {
        if (el !== portalTarget) el.remove();
      });

      const header = document.querySelector('.page-header');
      if (!header) { setPortalTarget(null); return; }
      let container = header.nextElementSibling;
      if (!container || !container.hasAttribute('data-breadcrumb-portal')) {
        container = document.createElement('div');
        container.setAttribute('data-breadcrumb-portal', 'true');
        header.parentNode.insertBefore(container, header.nextSibling);
      }
      setPortalTarget(container);
    };
    // Small delay to let Outlet render the PageHeader first
    findTarget();
    const timer = setTimeout(findTarget, 150);
    // Depuis le chargement des pages à la demande, la première visite d'une
    // page charge un chunk : 150 ms ne suffisent plus. PageHeader (shared-ui)
    // signale son montage ; on ré-ancre à ce moment-là. Pas de MutationObserver
    // (boucle infinie vécue le 17/05 : findTarget mute le DOM observé).
    window.addEventListener('page-header-mounted', findTarget);
    return () => { clearTimeout(timer); window.removeEventListener('page-header-mounted', findTarget); };
  }, [location.pathname, trail]);

  // Don't render if no trail — but keep hooks active above
  if (trail.length === 0) return null;

  // Build root crumbs basé sur le PREMIER path du trail (= point d'entrée
  // de la navigation, pas la page courante). Liste TOUS les SECTION_ROOTS
  // préfixes : ex "/parc/toners" -> ["/parc", "/parc/toners"].
  // Si le dernier ancestor matche exactement trail[0].path (cas section root
  // arrivée directe), on l'exclut car il sera affiché par le bloc "trail crumbs"
  // en mode actif. Sinon (cas page detail), on garde tous.
  const entryPath = trail[0].path;
  const ancestors = buildAncestors(entryPath);
  const lastAnc = ancestors[ancestors.length - 1];
  const rootCrumbs = (lastAnc && lastAnc.path === entryPath && trail.length === 1)
    ? ancestors.slice(0, -1)
    : ancestors;
  // firstSection conservé pour le calcul de section change dans le rendu trail.
  const firstSection = findSection(trail[0].path);

  // Page racine seule sans parent SECTION_ROOTS : le label de la section
  // sera affiché par le bloc trail crumbs en mode actif. Pas de return null
  // pour garantir un fil d'Ariane visible "où je suis" sur TOUTES les pages
  // (ex: /tickets affichera 'Tickets', /dashboard affichera 'Dashboard').

  const breadcrumbContent = (
    <nav style={navStyle} data-testid="breadcrumb">
      {/* Section root link(s) */}
      {rootCrumbs.map((root, i) => (
        <React.Fragment key={root.path}>
          {i > 0 && <ChevronRight size={11} style={{ color: '#c0cfe0', flexShrink: 0 }} />}
          <button
            onClick={() => navigate(root.path)}
            style={crumbBtnStyle}
            onMouseEnter={e => { e.currentTarget.style.color = '#2563a8'; e.currentTarget.style.background = '#e8f0fe'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#6b7d8f'; e.currentTarget.style.background = 'none'; }}
          >
            <Home size={12} style={{ flexShrink: 0 }} />
            {root.label}
          </button>
        </React.Fragment>
      ))}

      {/* Trail crumbs */}
      {trail.map((crumb, i) => {
        const isLast = i === trail.length - 1;
        // Skip trail[0] if it is already covered by rootCrumbs (avoid duplicate display).
        // Note : avec buildAncestors, rootCrumbs contient les préfixes du path courant SAUF
        // ce path lui-même, donc trail[0] = path courant n'est pas un doublon. On n'écarte
        // que si trail[0].path apparait explicitement dans rootCrumbs (cas cross-section).
        if (i === 0 && rootCrumbs.some(r => r.path === crumb.path)) {
          return null;
        }
        // Detect section change to show intermediate root
        const prevSection = i > 0 ? findSection(trail[i - 1].path) : firstSection;
        const curSection = findSection(crumb.path);
        const sectionChanged = curSection && prevSection && curSection !== prevSection;

        return (
          <React.Fragment key={crumb.path}>
            {/* If section changed, show intermediate root link.
                `curSection !== crumb.path` : quand la miette EST elle-même la
                racine de la nouvelle section (ex : /gestion/panier atteint
                depuis une opportunité), ce lien la rendait une seconde fois —
                « Opportunité › Panier › Panier ». Le lien intermédiaire ne sert
                qu'à situer une page DETAIL d'une autre section. */}
            {sectionChanged && curSection !== crumb.path && SECTION_ROOTS[curSection] && (
              <>
                <ChevronRight size={11} style={{ color: '#c0cfe0', flexShrink: 0 }} />
                <button
                  onClick={() => navigate(curSection)}
                  style={crumbBtnStyle}
                  onMouseEnter={e => { e.currentTarget.style.color = '#2563a8'; e.currentTarget.style.background = '#e8f0fe'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#6b7d8f'; e.currentTarget.style.background = 'none'; }}
                >
                  {SECTION_ROOTS[curSection].label}
                </button>
              </>
            )}
            <ChevronRight size={11} style={{ color: '#c0cfe0', flexShrink: 0 }} />
            {isLast ? (
              <span style={activeCrumbStyle}>
                {crumb.label}
              </span>
            ) : (
              <button
                onClick={() => {
                  navigateToCrumb(i);
                  navigate(crumb.path);
                }}
                style={crumbBtnStyle}
                onMouseEnter={e => { e.currentTarget.style.color = '#2563a8'; e.currentTarget.style.background = '#e8f0fe'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#6b7d8f'; e.currentTarget.style.background = 'none'; }}
              >
                {crumb.label}
              </button>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );

  // Use portal to render after PageHeader, or fallback to inline
  if (portalTarget) return createPortal(breadcrumbContent, portalTarget);
  return breadcrumbContent;
}

// ── Styles ───────────────────────────────────────────────────────────────────

const navStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  flexWrap: 'wrap',
  padding: '6px 24px',
  fontSize: 12,
  fontFamily: "'IBM Plex Sans', sans-serif",
  borderBottom: '1px solid #e0e7ef',
  background: '#f8fafc',
  minHeight: 32,
};

const crumbBtnStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: '#6b7d8f',
  fontSize: 12,
  padding: '2px 6px',
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  fontFamily: "'IBM Plex Sans', sans-serif",
  transition: 'color 0.15s, background 0.15s',
  whiteSpace: 'nowrap',
  maxWidth: 220,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const activeCrumbStyle = {
  color: '#1a3a5c',
  fontWeight: 600,
  fontSize: 12,
  padding: '2px 6px',
  whiteSpace: 'nowrap',
  maxWidth: 300,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'inline-block',
};
